// Task 196 D5 — Screenshot AI analysis (POC) panel + buttons.
//
// Throwaway UI. Lives inside the screenshot-tile DOM in the row-expand
// panel from task 192 (UnmatchedGroupsTable.tsx). Three buttons (Run AI
// analysis, Re-run, Refresh) drive the POST + GET routes; the panel
// renders persisted blobs grouped by `(promptId, model)` with prior
// runs nested under newest-first.
//
// Tokens-only styling, no new design language. Manual refresh is the
// POC posture; auto-poll is explicitly out-of-scope (the operator
// iterates prompts and a deliberate "let me check" cadence matches
// the workflow). Auto-fetch on first render shows existing blobs
// without re-issuing LLM calls.

import { useEffect, useState } from 'react';
import { useToken } from '@/lib/useToken';
import { apiFetch, ApiError } from '@/lib/api';
import styles from './ScreenshotAnalysisPanel.module.css';

interface AnalysisRun {
  promptId: string;
  promptLabel: string;
  promptSystem?: string;
  promptUserText: string;
  modelRequested: string;
  screenshotId: string;
  screenshotS3Key: string;
  issuedAt: string;
  response: {
    content: string;
    stopReason: string;
    usage: { inputTokens: number; outputTokens: number };
    model: string;
  };
}

interface ModelGroup {
  model: string;
  runs: AnalysisRun[];
}

interface PromptGroup {
  promptId: string;
  promptLabel: string;
  models: ModelGroup[];
}

interface AnalysesResponse {
  analyses: PromptGroup[];
}

interface AnalyzeResponse {
  enqueued: Array<{ promptId: string; model: string; jobId: string }>;
  issuedAt: string;
}

interface ScreenshotAnalysisPanelProps {
  screenshotId: string;
}

export function ScreenshotAnalysisPanel({ screenshotId }: ScreenshotAnalysisPanelProps) {
  const getToken = useToken();
  const [analyses, setAnalyses] = useState<PromptGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [enqueueState, setEnqueueState] = useState<{
    inFlight: boolean;
    lastEnqueuedCount: number | null;
    lastIssuedAt: string | null;
    error: string | null;
  }>({ inFlight: false, lastEnqueuedCount: null, lastIssuedAt: null, error: null });

  // First-render fetch: re-opening a tile shows prior runs without
  // re-issuing LLM calls.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const token = await getToken();
        const data = await apiFetch<AnalysesResponse>(
          `/api/v1/screenshots/${screenshotId}/analyses`,
          token,
        );
        if (!cancelled) setAnalyses(data.analyses);
      } catch (err) {
        // Initial-load failures are silent in the panel — operator
        // clicks Refresh to retry, surfaces the same error path.
        if (!cancelled) {
          setEnqueueState((s) => ({
            ...s,
            error: err instanceof Error ? err.message : 'Failed to load analyses',
          }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [screenshotId, getToken]);

  const handleRefresh = async () => {
    setLoading(true);
    setEnqueueState((s) => ({ ...s, error: null }));
    try {
      const token = await getToken();
      const data = await apiFetch<AnalysesResponse>(
        `/api/v1/screenshots/${screenshotId}/analyses`,
        token,
      );
      setAnalyses(data.analyses);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh analyses';
      setEnqueueState((s) => ({ ...s, error: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleEnqueue = async () => {
    setEnqueueState((s) => ({ ...s, inFlight: true, error: null }));
    try {
      const token = await getToken();
      const data = await apiFetch<AnalyzeResponse>(
        `/api/v1/screenshots/${screenshotId}/analyze`,
        token,
        { method: 'POST', body: JSON.stringify({}) },
      );
      setEnqueueState({
        inFlight: false,
        lastEnqueuedCount: data.enqueued.length,
        lastIssuedAt: data.issuedAt,
        error: null,
      });
    } catch (err) {
      let message: string;
      if (err instanceof ApiError) {
        message = `${err.status}: ${err.message}`;
      } else if (err instanceof Error) {
        message = err.message;
      } else {
        message = 'Failed to enqueue analysis';
      }
      setEnqueueState((s) => ({ ...s, inFlight: false, error: message }));
    }
  };

  const hasResults = analyses.length > 0;

  return (
    <div className={styles.panel} data-testid={`screenshot-analysis-${screenshotId}`}>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.btn}
          onClick={handleEnqueue}
          disabled={enqueueState.inFlight}
        >
          {hasResults ? 'Re-run AI analysis' : 'Run AI analysis'}
        </button>
        <button type="button" className={styles.btn} onClick={handleRefresh} disabled={loading}>
          Refresh
        </button>
        {enqueueState.lastEnqueuedCount !== null && (
          <span className={styles.status}>
            {enqueueState.lastEnqueuedCount} job{enqueueState.lastEnqueuedCount === 1 ? '' : 's'}{' '}
            enqueued — refresh to see results
          </span>
        )}
        {loading && <span className={styles.status}>Loading…</span>}
        {enqueueState.error && <span className={styles.error}>Error: {enqueueState.error}</span>}
      </div>
      {hasResults && (
        <div className={styles.groups}>
          {analyses.map((group) => (
            <details key={group.promptId} className={styles.group}>
              <summary className={styles.groupSummary}>
                {group.promptLabel} ({group.models.reduce((n, m) => n + m.runs.length, 0)} run
                {group.models.reduce((n, m) => n + m.runs.length, 0) === 1 ? '' : 's'})
              </summary>
              {group.models.map((modelGroup) => (
                <div key={modelGroup.model} className={styles.modelBlock}>
                  <div className={styles.modelLabel}>model: {modelGroup.model}</div>
                  {modelGroup.runs.map((run, idx) => (
                    <RunBlock key={`${run.issuedAt}-${idx}`} run={run} latest={idx === 0} />
                  ))}
                </div>
              ))}
            </details>
          ))}
        </div>
      )}
      {!hasResults && !loading && (
        <span className={styles.status}>
          No prior runs. Click <strong>Run AI analysis</strong> to enqueue all configured prompt ×
          model jobs.
        </span>
      )}
    </div>
  );
}

interface RunBlockProps {
  run: AnalysisRun;
  latest: boolean;
}

function RunBlock({ run, latest }: RunBlockProps) {
  return (
    <div className={styles.run}>
      <div className={styles.runMeta}>
        {latest ? '↳ latest · ' : '↳ '} issuedAt={run.issuedAt} · in=
        {run.response.usage.inputTokens} · out={run.response.usage.outputTokens} · stop=
        {run.response.stopReason}
      </div>
      <div className={styles.runContent}>{run.response.content}</div>
      <details className={styles.rawJson}>
        <summary>Raw JSON</summary>
        <pre>{JSON.stringify(run, null, 2)}</pre>
      </details>
    </div>
  );
}
